import React, { useState, useEffect } from 'react';
import { Button, Grid, Skeleton, Stack, Typography } from '@mui/material';
import { FormDatePicker } from '../common/Form/FormDatePicker';
import { JobReportCard } from './JobReportCard';
import { getData } from '../../DataAccessLayer';
import { Time } from '../common/MomentTime';
import PortalAlert from '../common/PortalAlert';
import ApiErrorMessage from '../common/ApiErrorMessage';
import { getJobReportConfig } from './config';

export const JobReport = ({ id = 'jobReport', widgetName = 'Processing Report' }) => {
  const [jobs, setJobs] = useState([]);
  const [startDate, setStartDate] = useState(Time.University(null, true).subtract(1, 'day'));

  const [endDate, setEndDate] = useState(Time.University(null, true));

  const maxDate = Time.University(null, true);

  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [cachedData, setCachedData] = useState({});

  const isEndDateValid = endDate.startOf('day').isAfter(startDate.startOf('day'));

  const jobReportConfig = getJobReportConfig({})[id];

  useEffect(() => {
    loadPageData();
  }, []);

  const loadPageData = async () => {
    await getJobs();
  };

  const getJobs = async () => {
    setIsError(false);
    setIsLoading(true);

    try {
      const data = await getData(
        jobReportConfig.getJobs(startDate.format('YYYY-MM-DD'), endDate.format('YYYY-MM-DD')),
        true,
      );

      setJobs(data);
    } catch (err) {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const addCachedData = ({ jobId, data }) => {
    const newCachedData = cachedData;
    newCachedData[jobId] = data;

    setCachedData(newCachedData);
  };

  return (
    <div className="myOdu__report">
      {(isError && (
        <PortalAlert id={`${id}__portalAlert`} severity="error" type="statusAlert">
          <ApiErrorMessage
            id={`${id}__apiErrorMessage`}
            reload={loadPageData}
            widgetName={widgetName}
            isPage={true}
          />
        </PortalAlert>
      )) || (
        <Stack direction={'column'} spacing={2}>
          <Grid
            container
            spacing={1}
            alignItems={{
              lg: 'center',
            }}
            direction={{ xs: 'column', md: 'column', lg: 'row' }}
          >
            <Grid
              item
              xs={12}
              md={'auto'}
              lg={'auto'}
              paddingLeft={{
                xs: '0 !important',
                md: '8px !important',
              }}
            >
              <Typography className="myOdu__label data">From</Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              lg={3}
              paddingLeft={{
                xs: '0 !important',
                md: '8px !important',
              }}
            >
              <FormDatePicker
                inputGridProps={{ xs: 12 }}
                value={startDate}
                onChange={(value) => setStartDate(value)}
                maxDate={maxDate}
              />
            </Grid>
            <Grid
              item
              xs={'auto'}
              paddingLeft={{
                xs: '0 !important',
                md: '8px !important',
              }}
            >
              <Typography className="myOdu__label data">To</Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              lg={3}
              paddingLeft={{
                xs: '0 !important',
                md: '8px !important',
              }}
            >
              <FormDatePicker
                inputGridProps={{ xs: 12 }}
                value={endDate}
                onChange={(value) => setEndDate(value)}
                minDate={startDate}
                maxDate={maxDate}
                error={!isEndDateValid}
                errorText={'End date must be after start date.'}
              />
            </Grid>
            <Grid
              item
              paddingLeft={{
                xs: '0 !important',
                md: '8px !important',
              }}
            >
              <Button
                variant="outlined"
                onClick={() => getJobs()}
                size="medium"
                disabled={!isEndDateValid}
                fullWidth
              >
                Get Reports
              </Button>
            </Grid>
          </Grid>

          <Grid container direction={'column'} spacing={1} sx={{ width: '100%' }}>
            {jobs.length > 0 &&
              jobs.map((record, index) => {
                return (
                  <Grid
                    item
                    xs={12}
                    sx={{
                      width: '100%',
                      paddingLeft: '0px !important',
                    }}
                  >
                    <JobReportCard
                      id={id}
                      record={record}
                      isExpanded={index === 0}
                      cachedData={cachedData}
                      addCachedData={addCachedData}
                    />
                  </Grid>
                );
              })}
            {jobs.length === 0 && !isLoading && (
              <Grid item xs={12}>
                <Typography component={'p'} variant={'p'}>
                  No jobs found for the given date range.
                </Typography>
              </Grid>
            )}
            {isLoading &&
              new Array(4).fill(0).map((_, index) => {
                return (
                  <Grid item>
                    <Skeleton
                      variant="rectangular"
                      height={index === 0 ? 100 : 50}
                      sx={{ borderRadius: 2 }}
                      animation={'wave'}
                    />
                  </Grid>
                );
              })}
          </Grid>
        </Stack>
      )}
    </div>
  );
};
